<template>
  <ContentWrap>
    <el-alert
      type="info"
      :closable="false"
      show-icon
      class="mb-12px"
      title="公共协议中心：按租户配置，可按「模块管理」中的模块覆盖。适用模块请选「租户默认」或具体模块（如 business）。保存后再次打开应能回显所选模块。"
    />
    <el-form :inline="true" :model="queryParams" class="-mb-15px" @submit.prevent>
      <el-form-item label="协议编码">
        <el-select v-model="queryParams.code" clearable placeholder="全部" class="!w-160px">
          <el-option
            v-for="dict in getStrDictOptions(DICT_TYPE.SYSTEM_AGREEMENT_CODE)"
            :key="dict.value"
            :label="dict.label"
            :value="dict.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="标题">
        <el-input v-model="queryParams.title" clearable placeholder="标题" class="!w-160px" />
      </el-form-item>
      <el-form-item label="适用模块">
        <el-select
          v-model="queryParams.moduleCode"
          clearable
          filterable
          placeholder="全部"
          class="!w-200px"
        >
          <el-option label="租户默认" value="__DEFAULT__" />
          <el-option
            v-for="mod in moduleOptions"
            :key="mod.name"
            :label="`${mod.title}（${mod.name}）`"
            :value="mod.name"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="状态">
        <el-select v-model="queryParams.status" clearable placeholder="全部" class="!w-120px">
          <el-option
            v-for="dict in getIntDictOptions(DICT_TYPE.COMMON_STATUS)"
            :key="dict.value"
            :label="dict.label"
            :value="dict.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="handleQuery">
          <Icon icon="ep:search" />搜索
        </el-button>
        <el-button @click="resetQuery">
          <Icon icon="ep:refresh" />重置
        </el-button>
        <el-button
          type="success"
          plain
          v-hasPermi="['portal:policy-agreement:create']"
          @click="openForm()"
        >
          <Icon icon="ep:plus" />新增协议
        </el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <ContentWrap>
    <el-table v-loading="loading" :data="list" border>
      <el-table-column prop="id" label="编号" width="70" />
      <el-table-column label="编码" width="120">
        <template #default="{ row }">
          <dict-tag :type="DICT_TYPE.SYSTEM_AGREEMENT_CODE" :value="row.code" />
        </template>
      </el-table-column>
      <el-table-column prop="title" label="标题" min-width="120" show-overflow-tooltip />
      <el-table-column label="适用范围" min-width="160" show-overflow-tooltip>
        <template #default="{ row }">
          <el-tag v-if="!row.moduleCode" type="info" size="small">租户默认</el-tag>
          <span v-else>{{ moduleLabel(row.moduleCode) }}</span>
        </template>
      </el-table-column>
      <el-table-column prop="version" label="版本" width="140" show-overflow-tooltip />
      <el-table-column label="状态" width="90">
        <template #default="{ row }">
          <dict-tag :type="DICT_TYPE.COMMON_STATUS" :value="row.status" />
        </template>
      </el-table-column>
      <el-table-column prop="sort" label="排序" width="70" />
      <el-table-column prop="updateTime" label="更新时间" width="170" :formatter="dateFormatter" />
      <el-table-column label="操作" width="160" fixed="right">
        <template #default="{ row }">
          <el-button
            link
            type="primary"
            v-hasPermi="['portal:policy-agreement:update']"
            @click="openForm(row)"
          >
            编辑
          </el-button>
          <el-button
            link
            type="danger"
            v-hasPermi="['portal:policy-agreement:delete']"
            @click="handleDelete(row)"
          >
            删除
          </el-button>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      v-model:limit="queryParams.pageSize"
      v-model:page="queryParams.pageNo"
      :total="total"
      @pagination="getList"
    />
  </ContentWrap>

  <el-dialog
    v-model="formOpen"
    :title="form.id ? '编辑协议' : '新增协议'"
    width="860px"
    destroy-on-close
    top="4vh"
  >
    <el-form ref="formRef" :model="form" :rules="rules" label-width="100px">
      <el-form-item label="协议编码" prop="code">
        <el-select
          v-model="form.code"
          filterable
          placeholder="请选择协议编码"
          class="!w-360px"
          @change="onCodeChange"
        >
          <el-option
            v-for="dict in getStrDictOptions(DICT_TYPE.SYSTEM_AGREEMENT_CODE)"
            :key="dict.value"
            :label="dict.label"
            :value="dict.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="展示标题" prop="title">
        <el-input v-model="form.title" class="!w-360px" placeholder="默认取字典名称" />
      </el-form-item>
      <el-form-item label="适用模块" prop="moduleCode">
        <el-select
          v-model="form.moduleCode"
          filterable
          placeholder="请选择适用模块"
          class="!w-360px"
        >
          <!-- 不用空串作 value：Element Plus 会当成未选，看起来像每次被清空 -->
          <el-option label="租户默认（全部小程序）" value="__DEFAULT__" />
          <el-option
            v-for="mod in moduleOptions"
            :key="mod.name"
            :label="`${mod.title}（${mod.name}）`"
            :value="mod.name"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="版本" prop="version">
        <el-input
          v-model="form.version"
          class="!w-360px"
          placeholder="可手填；改内容不填则自动生成"
        />
      </el-form-item>
      <el-form-item label="状态" prop="status">
        <el-radio-group v-model="form.status">
          <el-radio
            v-for="dict in getIntDictOptions(DICT_TYPE.COMMON_STATUS)"
            :key="dict.value"
            :value="dict.value"
          >
            {{ dict.label }}
          </el-radio>
        </el-radio-group>
      </el-form-item>
      <el-form-item label="排序" prop="sort">
        <el-input-number v-model="form.sort" :min="0" class="!w-200px" />
      </el-form-item>
      <el-form-item label="备注" prop="remark">
        <el-input v-model="form.remark" class="!w-360px" />
      </el-form-item>
      <el-form-item label="编辑方式">
        <el-radio-group v-model="editMode">
          <el-radio-button value="rich">富文本</el-radio-button>
          <el-radio-button value="plain">全文粘贴</el-radio-button>
        </el-radio-group>
      </el-form-item>
      <el-form-item label="协议正文" prop="content">
        <Editor
          v-if="editMode === 'rich' && formOpen"
          v-model="form.content"
          editor-id="system-policy-agreement-editor"
          height="480px"
          :editor-config="agreementEditorConfig"
        />
        <el-input
          v-else
          v-model="form.content"
          type="textarea"
          :rows="18"
          :maxlength="-1"
          resize="vertical"
          placeholder="可直接粘贴完整协议正文"
        />
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="formOpen = false">取消</el-button>
      <el-button type="primary" :loading="saving" @click="submitForm">保存</el-button>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import type { FormInstance, FormRules } from 'element-plus'
import type { IEditorConfig } from '@wangeditor-next/editor'
import { ElMessage, ElMessageBox } from 'element-plus'
import { onMounted, reactive, ref } from 'vue'
import { dateFormatter } from '@/utils/formatTime'
import { DICT_TYPE, getIntDictOptions, getStrDictOptions } from '@/utils/dict'
import { Editor } from '@/components/Editor'
import * as ModuleApi from '@/api/system/module'
import {
  PolicyAgreementApi,
  type PolicyAgreementVO
} from '@/api/system/policyAgreement'

defineOptions({ name: 'SystemPolicyAgreementV2' })

function escapeHtml(text: string) {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

const agreementEditorConfig: Partial<IEditorConfig> = {
  maxLength: 0,
  placeholder: '请输入协议正文',
  customPaste(editor, event) {
    const html = event.clipboardData?.getData('text/html')?.trim() || ''
    const text = event.clipboardData?.getData('text/plain') || ''
    event.preventDefault()
    if (html) {
      editor.dangerouslyInsertHtml(html)
    } else if (text) {
      const body = text
        .split(/\n{2,}/)
        .map((p) => `<p>${escapeHtml(p).replace(/\n/g, '<br>')}</p>`)
        .join('')
      editor.dangerouslyInsertHtml(body || '<p></p>')
    }
    return false
  }
}

const editMode = ref<'rich' | 'plain'>('plain')
const loading = ref(false)
const saving = ref(false)
const list = ref<PolicyAgreementVO[]>([])
const total = ref(0)
const moduleOptions = ref<ModuleApi.ModuleVO[]>([])

const queryParams = reactive({
  pageNo: 1,
  pageSize: 20,
  code: undefined as string | undefined,
  title: undefined as string | undefined,
  moduleCode: undefined as string | undefined,
  status: undefined as number | undefined
})

const formOpen = ref(false)
const formRef = ref<FormInstance>()
const form = reactive<PolicyAgreementVO>({
  id: undefined,
  code: 'USER',
  title: '用户协议',
  moduleCode: '__DEFAULT__',
  version: '',
  status: 0,
  sort: 0,
  remark: '',
  content: ''
})

const rules: FormRules = {
  code: [{ required: true, message: '请选择协议编码', trigger: 'change' }],
  content: [
    {
      required: true,
      validator: (_rule, value, callback) => {
        const text = String(value || '')
          .replace(/<[^>]+>/g, '')
          .replace(/&nbsp;/gi, ' ')
          .trim()
        if (!text) {
          callback(new Error('请填写协议正文'))
          return
        }
        callback()
      },
      trigger: 'blur'
    }
  ]
}

function toFormModuleCode(code?: string | null) {
  return code ? code : '__DEFAULT__'
}

function toApiModuleCode(code?: string | null) {
  if (!code || code === '__DEFAULT__') return ''
  return code
}

function moduleLabel(code?: string) {
  if (!code) return '租户默认'
  const mod = moduleOptions.value.find((m) => m.name === code)
  return mod ? `${mod.title}（${mod.name}）` : code
}

function onCodeChange(code: string) {
  const dict = getStrDictOptions(DICT_TYPE.SYSTEM_AGREEMENT_CODE).find((d) => d.value === code)
  if (dict && (!form.title || !form.id)) {
    form.title = dict.label
  }
}

function ensureModuleOption(code?: string) {
  if (!code || code === '__DEFAULT__') return
  if (moduleOptions.value.some((m) => m.name === code)) return
  moduleOptions.value = [
    ...moduleOptions.value,
    { name: code, title: code } as ModuleApi.ModuleVO
  ]
}

async function loadModuleOptions() {
  try {
    moduleOptions.value = (await ModuleApi.getAvailableModuleList()) || []
  } catch {
    try {
      moduleOptions.value = (await ModuleApi.getEnabledModuleList()) || []
    } catch {
      moduleOptions.value = []
    }
  }
}

async function getList() {
  loading.value = true
  try {
    const params: Record<string, any> = { ...queryParams }
    if (params.moduleCode === '__DEFAULT__') {
      params.moduleCode = ''
    }
    const data = await PolicyAgreementApi.getPage(params)
    list.value = data?.list || []
    total.value = data?.total || 0
  } finally {
    loading.value = false
  }
}

function handleQuery() {
  queryParams.pageNo = 1
  getList()
}

function resetQuery() {
  queryParams.code = undefined
  queryParams.title = undefined
  queryParams.moduleCode = undefined
  queryParams.status = undefined
  handleQuery()
}

async function openForm(row?: PolicyAgreementVO) {
  let detail = row
  if (row?.id) {
    try {
      detail = (await PolicyAgreementApi.get(row.id)) || row
    } catch {
      detail = row
    }
  }
  form.id = detail?.id
  form.code = detail?.code || 'USER'
  form.title = detail?.title || ''
  form.moduleCode = toFormModuleCode(detail?.moduleCode)
  ensureModuleOption(detail?.moduleCode)
  form.version = detail?.version || ''
  form.status = detail?.status ?? 0
  form.sort = detail?.sort ?? 0
  form.remark = detail?.remark || ''
  form.content = detail?.content || ''
  if (!form.title) {
    onCodeChange(form.code || 'USER')
  }
  editMode.value = 'plain'
  formOpen.value = true
}

async function submitForm() {
  await formRef.value?.validate()
  saving.value = true
  try {
    const payload: PolicyAgreementVO = {
      id: form.id,
      code: form.code,
      title: form.title,
      moduleCode: toApiModuleCode(form.moduleCode),
      version: form.version || undefined,
      status: form.status ?? 0,
      sort: form.sort ?? 0,
      remark: form.remark,
      content: form.content
    }
    if (form.id) {
      await PolicyAgreementApi.update(payload)
      ElMessage.success('已更新')
    } else {
      await PolicyAgreementApi.create(payload)
      ElMessage.success('已创建')
    }
    formOpen.value = false
    await getList()
  } finally {
    saving.value = false
  }
}

async function handleDelete(row: PolicyAgreementVO) {
  if (!row.id) return
  await ElMessageBox.confirm(`确认删除「${row.title || row.code}」？`, '提示', { type: 'warning' })
  await PolicyAgreementApi.delete(row.id)
  ElMessage.success('已删除')
  await getList()
}

onMounted(async () => {
  await loadModuleOptions()
  await getList()
})
</script>
